import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Typography } from '@mui/material';
import { ModalCDS, ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { enrollPasskeyAction } from 'store/auth/actions';

import {
  ENROLL_PASSKEY_MODAL_ID,
  PASSKEY_NICKNAME_LABEL,
  PASSKEY_NICKNAME_MAX_LENGTH,
  PASSKEY_NICKNAME_PLACEHOLDER,
} from './EnrollPasskeyModal.constants';
import { enrollPasskeyBodyStyles, enrollPasskeyErrorStyles } from './EnrollPasskeyModal.styles';
import { EnrollPasskeyModalTestIds } from './EnrollPasskeyModal.testIds';
import { getEnrollPasskeyErrorState } from './EnrollPasskeyModal.utils';

const EnrollPasskeyModal = ({ isOpen, onClose, onEnrolled }) => {
  const dispatch = useDispatch();

  const [isEnrolling, setIsEnrolling] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [nickname, setNickname] = useState('');

  const onNicknameChange = useCallback(e => setNickname(e.target.value), []);

  const onAcceptClick = useCallback(async () => {
    setIsEnrolling(true);
    setFeedback(null);

    const trimmedNickname = nickname.trim() || null;

    try {
      await dispatch(enrollPasskeyAction(trimmedNickname));

      if (onEnrolled) {
        onEnrolled(trimmedNickname);
      }

      onClose();
    } catch (error) {
      // Keep the modal open so the user can retry or dismiss. getEnrollPasskeyErrorState maps known
      // WebAuthn failures to precise copy and flags InvalidStateError (a passkey already exists on this
      // device) as the EXPECTED, non-failure case so it renders neutrally rather than as a red error.
      // For anything unrecognized it appends the raw name+message — the native Capacitor shell has no
      // console and the App Insights browser SDK is force-disabled there, so that on-screen detail is the
      // only channel exposing an unknown on-device failure (DEV-4084).
      setFeedback(getEnrollPasskeyErrorState(error));
    } finally {
      setIsEnrolling(false);
    }
  }, [dispatch, nickname, onClose, onEnrolled]);

  const onAfterClose = useCallback(() => {
    setNickname('');
    setFeedback(null);
    onClose();
  }, [onClose]);

  const body = useCallback(
    () => (
      <div style={enrollPasskeyBodyStyles}>
        <Typography color="text.secondary" variant="medium14">
          Set up a passkey on this device to sign in with your fingerprint, face, or security key —
          no verification code needed next time.
        </Typography>

        <TextFieldCDS
          dataTestId={EnrollPasskeyModalTestIds.NICKNAME_INPUT}
          label={PASSKEY_NICKNAME_LABEL}
          maxLength={PASSKEY_NICKNAME_MAX_LENGTH}
          name="passkeyNickname"
          placeholder={PASSKEY_NICKNAME_PLACEHOLDER}
          value={nickname}
          onChange={onNicknameChange}
        />

        {feedback && (
          <Typography
            color={feedback.isAlreadyEnrolled ? 'text.secondary' : 'error.main'}
            style={enrollPasskeyErrorStyles}
            variant="medium14"
          >
            {feedback.message}
          </Typography>
        )}
      </div>
    ),
    [feedback, nickname, onNicknameChange],
  );

  return (
    <ModalCDS
      isRestyled
      keepMounted
      withButtons
      acceptButtonDataTestId={EnrollPasskeyModalTestIds.ACCEPT_BUTTON}
      afterCloseCallback={onAfterClose}
      body={body}
      closeButtonDataTestId={EnrollPasskeyModalTestIds.CLOSE_BUTTON}
      dataTestId={EnrollPasskeyModalTestIds.MODAL}
      header="Set up a passkey"
      id={ENROLL_PASSKEY_MODAL_ID}
      isAcceptButtonDisabled={isEnrolling}
      open={isOpen}
      size={ModalSize.SMALL}
      onAcceptButtonName={isEnrolling ? 'Setting up…' : 'Set up passkey'}
      onAcceptClick={onAcceptClick}
    />
  );
};

EnrollPasskeyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEnrolled: PropTypes.func,
};

export default EnrollPasskeyModal;
